// Phone Companies list (view 'companies') — window.PhoneCompanies.
//
// Search-first: a server search box (/api/companies `q`: name, website, aka),
// the same Active / Lead pool / Archived / All buckets as CompaniesView, and
// one row per company (logo · name · "industry · city, ST" · revenue, owner,
// last touch). Pages in 40 at a time as you scroll. Tap opens the company.
//
// Deliberately NOT here: bulk select / promote, import, export, columns, save
// as view — desk work. This page never touches useColumnPrefs or any desktop
// layout key; what it remembers lives in memory for this session only.

const PC_PAGE = 40;
const PC_BUCKETS = [
  { value: 'active', label: 'Active' },
  { value: 'lead_pool', label: 'Lead pool' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];
// Server-side sorts (COMPANY_SERVER_SORT in companies.jsx). Revenue desc is
// the desktop default — "my biggest companies first".
const PC_SORTS = [
  { value: 'revenue', label: 'Revenue', by: 'revenue_est', dir: 'desc' },
  { value: 'touch', label: 'Last touch', by: 'last_interaction_at', dir: 'desc' },
  { value: 'name', label: 'Name', by: 'name', dir: 'asc' },
];

// In-memory only: coming back from a company lands on the same search,
// bucket and scroll position instead of a fresh list. The remembered rows
// are keyed by the signed-in user too (my_team is per user), and dropped
// whenever Atlas says data changed (an edit elsewhere, the app resuming).
const pcMemo = { q: '', bucket: 'active', sort: 'revenue', mine: false, key: null, items: null, total: 0, page: 0, loadedAll: false, scrollTop: 0 };
window.addEventListener('atlas-data-changed', () => {
  Object.assign(pcMemo, { key: null, items: null, total: 0, page: 0, loadedAll: false, scrollTop: 0 });
});

const pcOwnerShort = (name) => {
  if (!name || name === '—') return null;
  const parts = String(name).trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
};

const PhoneCompanyRow = React.memo(({ c, onOpen }) => {
  const kit = window.CompanyPhoneKit || {};
  const raw = c._raw || {};
  const place = kit.cityStateShort ? kit.cityStateShort(raw.hq_city, raw.hq_state) : (c.hq !== '—' ? c.hq : '');
  const sub = [c.industry !== '—' ? c.industry : null, place].filter(Boolean).join(' · ');
  const touch = kit.shortAgo ? kit.shortAgo(c.lastInteractionAt) : null;
  const owner = pcOwnerShort(c.owner);
  const rev = c.revenue != null && c.revenue !== '' ? window.fmtMoney(c.revenue) : null;
  return (
    <window.PhoneRow onClick={() => onOpen(c.id)} dim={!!raw.is_dnc}
      leading={<Logo name={c.name} color={c.logo} size={36} />}
      title={c.name}
      subtitle={sub || <span style={{ color: 'var(--text-4)' }}>No industry or location</span>}
      chevron={false}
      meta={(
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, maxWidth: 110 }}>
          <span className="num" style={{ fontSize: 14, color: rev ? 'var(--text-2)' : 'var(--text-4)', fontWeight: 500 }}>{rev || '—'}</span>
          <span style={{ fontSize: 12, color: 'var(--text-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
            {[owner, touch].filter(Boolean).join(' · ') || 'unowned'}
          </span>
        </span>
      )} />
  );
});

const PhoneCompanies = ({ goToCompany, activeUser }) => {
  const [q, setQ] = React.useState(pcMemo.q);
  const [qq, setQQ] = React.useState(pcMemo.q.trim());       // debounced term
  const [bucket, setBucket] = React.useState(pcMemo.bucket);
  const [sort, setSort] = React.useState(pcMemo.sort);
  const [mine, setMine] = React.useState(pcMemo.mine && !!activeUser);
  const [sheet, setSheet] = React.useState(false);
  const [counts, setCounts] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [paging, setPaging] = React.useState(false);
  const scrollRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const reqSeq = React.useRef(0);

  React.useEffect(() => {
    const t = setTimeout(() => setQQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const sortDef = PC_SORTS.find((s) => s.value === sort) || PC_SORTS[0];
  const ownerId = mine && activeUser ? activeUser.id : null;
  const params = React.useMemo(() => {
    const p = { my_team: true, order_by: sortDef.by, order_dir: sortDef.dir };
    if (bucket !== 'all') p.pipeline_state = bucket;
    if (qq) p.q = qq;
    if (ownerId) p.assigned_user_id = ownerId;
    return p;
  }, [bucket, qq, ownerId, sortDef.by, sortDef.dir]);
  const key = JSON.stringify(params);
  const memoKey = `${(activeUser && activeUser.id) || ''}|${key}`;

  // A remembered list for this exact query (and user) is shown straight
  // away, scrolled back to where it was, and page 1 is quietly re-read
  // underneath; anything else starts from page 1.
  const resumed = pcMemo.key === memoKey && pcMemo.items != null;
  const [list, setList] = React.useState(() => (resumed
    ? { key, items: pcMemo.items, total: pcMemo.total, page: pcMemo.page, loadedAll: pcMemo.loadedAll }
    : { key, items: null, total: 0, page: 0, loadedAll: false }));

  const fetchPage = React.useCallback(async (page, append) => {
    const seq = ++reqSeq.current;
    setPaging(true);
    if (!append) setError(null);
    try {
      const res = await window.AtlasApi.listCompanies({ ...JSON.parse(key), page, limit: PC_PAGE });
      if (seq !== reqSeq.current) return;   // superseded by a newer query
      const rows = ((res && res.items) || []).map(window.AtlasData.__adaptCompany);
      const total = (res && res.total) != null ? res.total : rows.length;
      setList((prev) => {
        const items = append && prev.key === key && prev.items ? [...prev.items, ...rows] : rows;
        return { key, items, total, page, loadedAll: rows.length === 0 || items.length >= total };
      });
    } catch (e) {
      if (seq !== reqSeq.current) return;
      if (append) {
        window.pushToast && window.pushToast({ type: 'error', title: 'Could not load more', body: e.message });
        setList((prev) => ({ ...prev, loadedAll: true }));
      } else {
        setError(e.message || 'Could not load companies');
        setList({ key, items: [], total: 0, page: 0, loadedAll: true });
      }
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [key]);

  const firstRun = React.useRef(true);
  React.useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      if (resumed) {
        const el = scrollRef.current;
        if (el) el.scrollTop = pcMemo.scrollTop || 0;
        // Quiet refresh: no spinner, no error state. Fresh page-1 rows
        // replace the first page; the deeper pages already loaded stay so
        // the scroll position holds. Dropped if any other fetch started.
        const seq = reqSeq.current;
        window.AtlasApi.listCompanies({ ...JSON.parse(key), page: 1, limit: PC_PAGE })
          .then((res) => {
            if (seq !== reqSeq.current) return;
            const rows = ((res && res.items) || []).map(window.AtlasData.__adaptCompany);
            const total = (res && res.total) != null ? res.total : rows.length;
            setList((prev) => {
              if (prev.key !== key || !prev.items) return prev;
              const fresh = new Set(rows.map((r) => r.id));
              const items = [...rows, ...prev.items.slice(PC_PAGE).filter((r) => !fresh.has(r.id))];
              return { ...prev, items, total, page: Math.max(prev.page, 1), loadedAll: rows.length === 0 || items.length >= total };
            });
          })
          .catch(() => {});
        return;
      }
    }
    setList({ key, items: null, total: 0, page: 0, loadedAll: false });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    fetchPage(1, false);
  }, [memoKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Remember the query + what's loaded for the trip back from a company.
  React.useEffect(() => {
    Object.assign(pcMemo, { q, bucket, sort, mine });
  }, [q, bucket, sort, mine]);
  React.useEffect(() => {
    if (list.key !== key || list.items == null) return;
    Object.assign(pcMemo, { key: memoKey, items: list.items, total: list.total, page: list.page, loadedAll: list.loadedAll });
  }, [list, key, memoKey]);

  // Bucket counts (no search applied — they label the chips). Re-read when
  // "Mine" flips, since that narrows every bucket.
  React.useEffect(() => {
    let alive = true;
    const base = { my_team: true, limit: 1 };
    if (ownerId) base.assigned_user_id = ownerId;
    const count = (p) => window.AtlasApi.listCompanies({ ...base, ...p }).then((r) => (r && r.total) || 0).catch(() => null);
    Promise.all([count({ pipeline_state: 'active' }), count({ pipeline_state: 'lead_pool' }), count({ pipeline_state: 'archived' }), count({})])
      .then(([active, lead_pool, archived, all]) => { if (alive) setCounts({ active, lead_pool, archived, all }); });
    return () => { alive = false; };
  }, [ownerId]);

  const items = list.key === key ? list.items : null;
  const hasMore = items != null && !list.loadedAll && items.length < list.total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || paging) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchPage(list.page + 1, true);
    }, { root: scrollRef.current || null, rootMargin: '400px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, list.page, fetchPage]);

  const open = React.useCallback((id) => goToCompany && goToCompany(id), [goToCompany]);
  const fmtN = (n) => (n == null ? null : n >= 10000 ? `${Math.round(n / 1000)}k` : n.toLocaleString());
  const bucketLabel = (PC_BUCKETS.find((b) => b.value === bucket) || {}).label || '';
  const narrowed = sort !== 'revenue' || mine;

  return (
    <window.PhoneScroll ref={scrollRef} onScroll={(e) => { pcMemo.scrollTop = e.currentTarget.scrollTop; }}>
      {/* Search + buckets stay pinned while the list scrolls. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg)', borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 16px 10px' }}>
          <window.PhoneSearchField value={q} onChange={setQ} placeholder="Search name or website" style={{ flex: 1, minWidth: 0 }} />
          <button type="button" aria-label="Sort and filter" onClick={() => setSheet(true)}
            style={{ WebkitTapHighlightColor: 'transparent', width: 44, height: 40, flexShrink: 0, borderRadius: 10, cursor: 'pointer',
                     border: '1px solid ' + (narrowed ? 'var(--accent)' : 'var(--border)'),
                     background: narrowed ? 'var(--accent-soft)' : 'var(--surface)', color: narrowed ? 'var(--accent-text)' : 'var(--text-2)',
                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Sort size={18} />
          </button>
        </div>
        <window.PhoneChips value={bucket} onChange={setBucket}
          options={PC_BUCKETS.map((b) => ({ ...b, count: !qq && counts ? fmtN(counts[b.value]) : null }))} />
      </div>

      <div style={{ padding: '10px 16px 6px', fontSize: 13, color: 'var(--text-3)', minHeight: 18 }}>
        {items == null ? 'Loading…'
          : error ? null
          : `${list.total.toLocaleString()} ${qq ? `match${list.total === 1 ? '' : 'es'}` : (list.total === 1 ? 'company' : 'companies')}`
            + `${mine ? ' · mine' : ''} · by ${sortDef.label.toLowerCase()}`}
      </div>

      {error && (
        <window.PhoneEmpty title="Couldn't load companies" body={error}
          action={<Btn variant="accent" onClick={() => fetchPage(1, false)}>Try again</Btn>} />
      )}
      {items == null && !error && (
        <div style={{ padding: '0 16px' }}>{window.SkeletonRows && <window.SkeletonRows rows={8} variant="company" />}</div>
      )}
      {items && items.length === 0 && !error && (
        <window.PhoneEmpty
          title={qq ? `No ${bucket === 'all' ? '' : bucketLabel.toLowerCase() + ' '}companies match “${qq}”` : `No ${bucketLabel.toLowerCase()} companies`}
          body={mine ? 'Only companies you own are shown.' : null}
          action={(bucket !== 'all' || mine) ? (
            <Btn variant="outline" onClick={() => { setBucket('all'); setMine(false); }}>Search all companies</Btn>
          ) : null} />
      )}
      {items && items.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {items.map((c) => <PhoneCompanyRow key={c.id} c={c} onOpen={open} />)}
        </div>
      )}
      {hasMore && (
        <div ref={sentinelRef} style={{ padding: '0 16px' }}>
          {window.SkeletonRows && <window.SkeletonRows rows={2} variant="company" />}
        </div>
      )}
      {items && items.length > 0 && !hasMore && (
        <div style={{ padding: '14px 16px', fontSize: 12.5, color: 'var(--text-4)', textAlign: 'center' }}>
          {items.length.toLocaleString()} of {list.total.toLocaleString()}
        </div>
      )}

      <window.PhoneSheet open={sheet} onClose={() => setSheet(false)} title="Sort & filter">
        <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Sort by</div>
            <window.PhoneSegmented value={sort} onChange={setSort}
              options={PC_SORTS.map((s) => ({ value: s.value, label: s.label }))} />
          </div>
          {activeUser && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Owner</div>
              <window.PhoneSegmented value={mine ? 'mine' : 'any'} onChange={(v) => setMine(v === 'mine')}
                options={[{ value: 'any', label: 'Anyone' }, { value: 'mine', label: 'Mine' }]} />
            </div>
          )}
        </div>
      </window.PhoneSheet>
    </window.PhoneScroll>
  );
};

window.PhoneCompanies = PhoneCompanies;
